// src/App.jsx
// ============================================================
// STOREFRONT PROMOTION — EA is now THE application.
// The EA experience is de-prefixed out of /ea/* to top-level routes:
//   - Step 1: Personalize          → /home  (EAPersonalizationPage)
//   - Step 2: Evaluate & Shortlist → /opportunities
//   - Step 3: Activate             → /activate
//   - Weight settings              → /weights
//   - Use case detail family       → /opportunity/:id/{brief,rationale,roi-model}
//   - Initiative Command Center    → /execute/:initiativeId
//   - Strategy Intelligence Brief  → /strategy-brief (entry point)
//   - Upgrade (conversion surface) → /upgrade
// A single pathless layout route mounts EAProvider + AppShell(hideSecondaryNav)
// once and swaps pages via <Outlet/>, preserving EA state across the flow.
// Legacy /ea/* paths redirect to the above via <EALegacyRedirect/>.
// Standalone printable pages are de-prefixed too (/report/print, /sep/print,
// /use-case/*/print) and render without AppShell.
//
// Legacy Freemium (/freemium/*) and Pro (/pro/*) routes remain for now and are
// removed in the deletion phase of the storefront promotion.
//
// Strategy Visualizations live under /viz/* (standalone, no auth/AppShell);
// legacy /market-opportunity alias preserved for backward compat.
// ============================================================

import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  Outlet,
  useParams,
  useLocation,
} from "react-router-dom";

import FinancialQAPage from "./pages/FinancialQAPage";

// Layout
import AppShell from "./layouts/AppShell";
import MainAppLayout from "./layouts/MainAppLayout";
import { ClientProvider } from "./contexts/ClientContext";
import { FilterProvider } from "./contexts/FilterContext";
import { PageContextProvider } from "./contexts/PageContext";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { HelpProvider } from "./help/HelpProvider";
import { DemoModeIndicator } from "./hooks/useDemoPlan";

// EA Focus Mode Layout Wrapper
import EAFocusRouteWrapper from "./prototype/layouts/EAFocusRouteWrapper";
import EADocumentUploadPage from "./prototype/pages/EADocumentUploadPage";

// Env (Firebase + Looker Studio)
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};
const lookerStudioEmbedUrl = import.meta.env.VITE_LOOKER_STUDIO_EMBED_URL;

// Pages
import MatrixPage from "./pages/MatrixPage.jsx";
import PlaybookPage from "./pages/PlaybookPage";
import StrategicAlignmentDashboard from "./pages/StrategicAlignmentDashboard";
import ClientConfiguratorPage from "./pages/ClientConfiguratorPage";
import ClientProfilePage from "./pages/ClientProfilePage";
import UseCaseResults from "./pages/UseCaseResults";
import ChatPage from "./pages/ChatPage";
import SettingsPage from "./pages/SettingsPage";
import LogoutPage from "./pages/LogoutPage";
import UseCaseProfilePage from "./pages/UseCaseProfilePage";
import UseCaseSelector from "./pages/UseCaseSelector";
import UseCaseQADashboard from "./pages/UseCaseQADashboard";
import PlaybookDraftPage from "./pages/PlaybookDraftPage";
import ClientPlaybooksPage from "./pages/ClientPlaybooksPage";
import CapabilitiesSummaryPage from "./pages/CapabilitiesSummaryPage";
import ComprehensiveStrategySummaryPage from "./pages/ComprehensiveStrategySummaryPage";
import StrategySummaryPage from "./pages/StrategySummaryPage";
import DocumentManagementPage from "./pages/DocumentManagementPage";
import ActionBoardPriority from "./pages/ActionBoardPriority";
import SetupPage from "./pages/SetupPage";

// Centralized Messages page (tier-aware)
import MessagesPage from "./pages/MessagesPage";



// Auth pages
import LoginPage from "./pages/LoginPage";
import SignupPage from "./pages/SignupPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import VerifyEmailPage from "./pages/VerifyEmailPage";
import VerifyInvitationPage from "./pages/VerifyInvitationPage";

// Subscription pages
import SubscriptionSuccessPage from "./pages/SubscriptionSuccessPage";
import SubscriptionCancelledPage from "./pages/SubscriptionCancelledPage";
import SubscriptionManagementPage from "./pages/SubscriptionManagementPage";

// Auth protection
import PrivateRoute from "./components/PrivateRoute";
import RoleProtectedRoute from "./components/RoleProtectedRoute";

// Unified survey (primary experience)
import UnifiedSurvey from "./components/UnifiedSurvey";
import UnifiedSurveyModal from "./components/UnifiedSurveyModal";

// Dev/Test survey
import StrategicPrioritiesSurvey from "./components/StrategicPrioritiesSurvey";

// EA Prototype (feature-flagged)
// UPDATED: Added EAWeightsPage for dedicated weight settings
// UPDATED: Added EAInitiativeCommandCenterPage for initiative drill-down
import {
  EAProvider,
  EAPersonalizationPage,
  EAOpportunitiesPage,
  EAUpgradePage,
  EAActivatePage,            // Serves /activate (Step 3: Activate)
  EAUseCaseHubPage,                 // Full-Content Drilldown v4 — Use-Case Hub (Brief/Scoring/ROI tabs)
  EAWeightsPage,                    // Dedicated weight settings page
} from './prototype';
// Execute hub (Oct 21 v3) — replaces the single-page Initiative Command Center.
import EAExecuteHubPage, { ExecuteHubRedirect } from './prototype/pages/EAExecuteHubPage';

import {
  EAPersonalizationVariantA,
  EAPersonalizationVariantB,
} from './prototype/variants/personalization';

// EA Strategy Intelligence Brief — prototype page.
// Entry point at /strategy-brief; its strategy-brief components are live EA surface.
import EAStrategyIntelligenceBriefPage from './prototype/pages/StrategyIntelligenceBriefPage';

// Enterprise Version prototype (Phase 2 shell) — gated by VITE_ENABLE_ENTERPRISE_PROTOTYPE.
// Empty stub surfaces; the shell (mode-flip + navy sub-nav) lives in src/prototype/enterprise.
import {
  PortfolioSurface,
  ScenariosSurface,
  DecisionsSurface,
  ReportsSurface,
} from './prototype/enterprise';

// EA Printable Report — standalone page, no AppShell
import EAPrintableReportPage from './prototype/pages/EAPrintableReportPage';

// EA Printable SEP — standalone page, no AppShell
import EAPrintableSEPPage from './prototype/pages/EAPrintableSEPPage';

// EA Printable Use Case Profile — standalone page, no AppShell
import EAPrintableUseCaseProfilePage from './prototype/pages/EAPrintableUseCaseProfilePage';

// EA Printable CFO ROI Summary — standalone page, no AppShell
import EAPrintableCFOSummaryPage from './prototype/pages/EAPrintableCFOSummaryPage';

// EA Printable Executive One-Pager — standalone page, no AppShell
import EAPrintableExecutiveOnePagerPage from './prototype/pages/EAPrintableExecutiveOnePagerPage';
import EAPrintableBoardRationalePage    from './prototype/pages/EAPrintableBoardRationalePage';
import EAPrintableAssumptionsAppendixPage from './prototype/pages/EAPrintableAssumptionsAppendixPage';

// Strategy Visualizations — standalone, no auth, no AppShell
// Located in /src/visualizations/ folder
import CrossingTheChasmVisualizer from "./visualizations/CrossingTheChasmVisualizer";
import SIPStrategicRoadmap from "./visualizations/SIPStrategicRoadmap";
import StrategyIntelligenceSystem from "./visualizations/StrategyIntelligenceSystem";
import LeadershipOrgChart from "./visualizations/LeadershipOrgChart";

import "./App.css";

/**
 * Route wrapper to render the UnifiedSurvey as a full page.
 */
function UnifiedSurveyRoute() {
  const { type } = useParams();
  const { session, sessionLoading } = useAuth();
  
  const clientId = session?.client_id || null;
  const userId = session?.user_id || "admin";

  if (sessionLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#18365E] mx-auto mb-4"></div>
          <p className="text-slate-600">Loading session...</p>
        </div>
      </div>
    );
  }

  if (!clientId) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center max-w-md p-6">
          <div className="text-amber-500 text-4xl mb-4">⚠️</div>
          <h2 className="text-xl font-semibold text-[#18365E] mb-2">No Organization Selected</h2>
          <p className="text-slate-600 mb-4">
            Please ensure you're logged in with an organization to access surveys.
          </p>
          <button
            onClick={() => window.location.assign('/home')}
            className="px-4 py-2 bg-[#18365E] text-white rounded-lg hover:bg-[#18365E]/90"
          >
            Return to Home
          </button>
        </div>
      </div>
    );
  }

  const getSurveyTitle = (surveyType) => {
    switch (surveyType) {
      case "strategy":
        return "Strategic Priorities Assessment";
      case "capabilities":
        return "Data & Analytics Capabilities";
      case "readiness":
        return "Data Readiness and Governance";
      default:
        return `${surveyType.charAt(0).toUpperCase() + surveyType.slice(1)} Survey`;
    }
  };

  return (
    <UnifiedSurvey
      isOpen={true}
      surveyType={type}
      surveyTitle={getSurveyTitle(type)}
      clientId={clientId}
      userId={userId}
      userProfile={{ client_id: clientId, user_id: userId }}
      onComplete={({ clientId: cid, surveyType: t }) => {
        window.location.assign(`/summary/${t}?client_id=${cid}`);
      }}
      onSaveAndExit={() => {
        window.history.back();
      }}
      allowEdit={true}
      showReview={true}
      enableDraftRecovery={true}
      enableKeyboardNavigation={true}
    />
  );
}

/**
 * Optional modal route: /survey/:type/modal
 */
function UnifiedSurveyModalRoute() {
  const { type } = useParams();
  const { session, sessionLoading } = useAuth();
  
  const clientId = session?.client_id || null;
  const userId = session?.user_id || "admin";

  if (sessionLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#18365E] mx-auto mb-4"></div>
          <p className="text-slate-600">Loading session...</p>
        </div>
      </div>
    );
  }

  if (!clientId) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center max-w-md p-6">
          <div className="text-amber-500 text-4xl mb-4">⚠️</div>
          <h2 className="text-xl font-semibold text-[#18365E] mb-2">No Organization Selected</h2>
          <p className="text-slate-600 mb-4">
            Please ensure you're logged in with an organization to access surveys.
          </p>
          <button
            onClick={() => window.history.back()}
            className="px-4 py-2 bg-[#18365E] text-white rounded-lg hover:bg-[#18365E]/90"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <UnifiedSurveyModal
      isOpen={true}
      surveyType={type}
      clientId={clientId}
      userId={userId}
      userProfile={{ client_id: clientId, user_id: userId }}
      onClose={() => {
        window.history.back();
      }}
      onComplete={({ clientId: cid, surveyType: t }) => {
        window.location.assign(`/summary/${t}?client_id=${cid}`);
      }}
      onSaveAndExit={() => {
        window.history.back();
      }}
    />
  );
}

/**
 * Wrapper for ClientProfilePage to extract clientId from params
 */
function ClientProfileRoute() {
  const { clientId } = useParams();
  return <ClientProfilePage clientId={String(clientId)} />;
}

/**
 * Wrapper for ClientProfilePage using session.client_id from AuthContext
 */
function ClientProfileAuthRoute() {
  const { session } = useAuth();
  return <ClientProfilePage clientId={session?.client_id} />;
}

/**
 * Wrapper for DocumentManagementPage using session.client_id from AuthContext
 */
function DocumentManagementAuthRoute() {
  const { session, sessionLoading } = useAuth();

  if (sessionLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#18365E] mx-auto mb-4"></div>
          <p className="text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  return <DocumentManagementPage clientId={session?.client_id} />;
}

/**
 * Wrapper for DocumentManagementPage to extract clientId from params
 */
function DocumentManagementRoute() {
  const { clientId } = useParams();
  return <DocumentManagementPage clientId={String(clientId)} />;
}

/**
 * Wrapper for StrategicAlignmentDashboard using session.client_id from AuthContext
 */
function StrategicAlignmentAuthRoute() {
  const { session, sessionLoading } = useAuth();

  if (sessionLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#18365E] mx-auto mb-4"></div>
          <p className="text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  return <StrategicAlignmentDashboard clientId={session?.client_id} useCaseId={10} />;
}

/**
 * Wrapper for ActionBoardPriority to extract clientId from params
 */
function ActionBoardRoute() {
  return <ActionBoardPriority />;
}

/**
 * Wrapper for test survey using session.client_id from AuthContext
 */
function TestSurveyRoute() {
  const { session, sessionLoading } = useAuth();

  if (sessionLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#18365E] mx-auto mb-4"></div>
          <p className="text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  const clientId = session?.client_id || "101";
  const userId = session?.user_id || "admin";

  return (
    <StrategicPrioritiesSurvey
      userProfile={{ client_id: clientId, user_id: userId }}
      onComplete={() => console.log("Test survey completed")}
      onSaveAndExit={() => console.log("Test survey saved and exited")}
      onGoToSummary={() => console.log("Go to summary")}
      initialResponses={{}}
      initialCompletionStatus={false}
    />
  );
}

/**
 * Legacy /ea/* → de-prefixed route redirect.
 * EA was promoted to the whole app; old /ea/* bookmarks and any residual
 * internal links resolve here to the new paths (route params preserved).
 */
function EALegacyRedirect() {
  const location = useLocation();
  const rest = location.pathname.replace(/^\/ea/, ""); // e.g. "/opportunities", "/use-case/opp-1/brief"
  const search = location.search || "";

  const map = (r) => {
    if (r === "" || r === "/") return "/home";
    if (r === "/context") return "/home";
    if (r.startsWith("/personalization")) return "/home"; // variants handled before map()
    // Detail-page renames (params preserved)
    let m;
    if ((m = r.match(/^\/roi-model\/([^/]+)/))) return `/opportunity/${m[1]}/roi-model`;
    if ((m = r.match(/^\/use-case\/([^/]+)\/(brief|rationale)/))) return `/opportunity/${m[1]}/${m[2]}`;
    // Print pages (de-prefixed, standalone)
    if (r.endsWith("/print")) return r;
    // Journey + settings
    if (r.startsWith("/opportunities") || r.startsWith("/decisions")) return "/opportunities";
    if (r.startsWith("/activate") || r.startsWith("/validation")) return "/activate";
    if (r.startsWith("/weights")) return r; // /weights, /weights/measures
    if (r.startsWith("/upgrade")) return "/upgrade";
    if (r.startsWith("/execute/")) return r; // /execute/:initiativeId
    return "/home";
  };

  const target = rest.startsWith("/personalization-variant") ? rest : map(rest);
  return <Navigate to={`${target}${search}`} replace />;
}

export default function App() {
  return (
    <AuthProvider>
      <ClientProvider>
        <FilterProvider>
          <PageContextProvider>
            {/* Presenter demo controls (visible in the demo build): plan/mode toggles + full reset. */}
            <DemoModeIndicator />
            <Router>
              {/* In-app help overlay: one provider above both layouts, inside the router so it
                  can resolve the page context from the location (src/help). */}
              <HelpProvider>
              <Routes>
                {/* Auth routes (no layout) */}
                <Route path="/login" element={<LoginPage />} />
                <Route path="/signup" element={<SignupPage />} />
                <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                <Route path="/verify-email" element={<VerifyEmailPage />} />
                <Route path="/verify-invitation" element={<VerifyInvitationPage />} />

                {/* Subscription flow pages (public) */}
                <Route path="/subscription/success" element={<SubscriptionSuccessPage />} />
                <Route path="/subscription/cancelled" element={<SubscriptionCancelledPage />} />

                {/* ============================================================ */}
                {/* STRATEGY VISUALIZATIONS — /viz/* namespace                   */}
                {/* Standalone, no auth, no AppShell. Use for investor decks,   */}
                {/* team alignment, and screenshot/PDF capture.                  */}
                {/* Source: /src/visualizations/                                 */}
                {/* ============================================================ */}
                <Route path="/viz/strategic-roadmap" element={<SIPStrategicRoadmap />} />
                <Route path="/viz/crossing-the-chasm" element={<CrossingTheChasmVisualizer />} />
                <Route path="/viz/sip-system" element={<StrategyIntelligenceSystem />} />
                <Route path="/viz/leadership-org-chart" element={<LeadershipOrgChart />} />

                {/* Legacy route — kept so existing bookmarks/links don't break */}
                <Route path="/market-opportunity" element={<CrossingTheChasmVisualizer />} />

                {/* Full-screen (no header) - Protected */}
                <Route path="/survey/:type" element={<PrivateRoute><UnifiedSurveyRoute /></PrivateRoute>} />
                <Route path="/survey/:type/modal" element={<PrivateRoute><UnifiedSurveyModalRoute /></PrivateRoute>} />
                <Route path="/setup" element={<PrivateRoute><SetupPage /></PrivateRoute>} />
                <Route path="/setup/:clientId" element={<PrivateRoute><SetupPage /></PrivateRoute>} />

                {/* /home is now owned by the EA app layout below         */}
                {/* (EAPersonalizationPage = Step 1). Legacy HomePage is   */}
                {/* retired as part of the storefront promotion.           */}

                {/* SUBSCRIPTION MANAGEMENT */}
                <Route
                  path="/subscription"
                  element={
                    <PrivateRoute>
                      <RoleProtectedRoute allowedRoles={["CLIENT_ADMIN"]}>
                        <AppShell>
                          <SubscriptionManagementPage />
                        </AppShell>
                      </RoleProtectedRoute>
                    </PrivateRoute>
                  }
                />

                {/* SHARED PERSONALIZE ROUTE — retired in favor of EA /home */}
                <Route path="/personalize" element={<Navigate to="/home" replace />} />
                <Route path="/personalize/*" element={<Navigate to="/home" replace />} />

                {/* Legacy Freemium routes — removed by storefront promotion.
                    Bookmarks redirect to the EA app. */}
                <Route path="/freemium/*" element={<Navigate to="/home" replace />} />

                {/* ============================================================ */}
                {/* EA PRINTABLE REPORT — STANDALONE (no AppShell)               */}
                {/* Opens in a new tab for print/PDF export.                     */}
                {/* Wrapped in EAProvider for safety (section components may     */}
                {/* reference context internally). No journey bar, no sidebar.   */}
                {/* ============================================================ */}
                {import.meta.env.VITE_ENABLE_EA_PROTOTYPE === 'true' && (
                  <Route
                    path="/report/print"
                    element={
                      <PrivateRoute>
                        <EAProvider>
                          <EAPrintableReportPage />
                        </EAProvider>
                      </PrivateRoute>
                    }
                  />
                )}

                {/* ============================================================ */}
                {/* EA PRINTABLE SEP — STANDALONE (no AppShell)                 */}
                {/* Opens in a new tab for print/PDF export.                     */}
                {/* ============================================================ */}
                {import.meta.env.VITE_ENABLE_EA_PROTOTYPE === 'true' && (
                  <Route
                    path="/sep/print"
                    element={
                      <PrivateRoute>
                        <EAProvider>
                          <EAPrintableSEPPage />
                        </EAProvider>
                      </PrivateRoute>
                    }
                  />
                )}

                {/* ============================================================ */}
                {/* EA PRINTABLE USE CASE PROFILE — STANDALONE (no AppShell)    */}
                {/* Opens in a new tab for print/PDF export.                     */}
                {/* ============================================================ */}
                {import.meta.env.VITE_ENABLE_EA_PROTOTYPE === 'true' && (
                  <Route
                    path="/use-case/print"
                    element={
                      <PrivateRoute>
                        <EAProvider>
                          <EAPrintableUseCaseProfilePage />
                        </EAProvider>
                      </PrivateRoute>
                    }
                  />
                )}

                {/* ============================================================ */}
                {/* EA PRINTABLE CFO ROI SUMMARY — STANDALONE (no AppShell)     */}
                {/* Opens in a new tab for print/PDF export.                     */}
                {/* ============================================================ */}
                {import.meta.env.VITE_ENABLE_EA_PROTOTYPE === 'true' && (
                  <Route
                    path="/use-case/cfo-summary/print"
                    element={
                      <PrivateRoute>
                        <EAProvider>
                          <EAPrintableCFOSummaryPage />
                        </EAProvider>
                      </PrivateRoute>
                    }
                  />
                )}

                {/* ============================================================ */}
                {/* EA PRINTABLE EXECUTIVE ONE-PAGER — STANDALONE (no AppShell) */}
                {/* Opens in a new tab for print/PDF export.                     */}
                {/* ============================================================ */}
                {import.meta.env.VITE_ENABLE_EA_PROTOTYPE === 'true' && (
                  <Route
                    path="/use-case/executive-summary/print"
                    element={
                      <PrivateRoute>
                        <EAProvider>
                          <EAPrintableExecutiveOnePagerPage />
                        </EAProvider>
                      </PrivateRoute>
                    }
                  />
                )}

                {/* ============================================================ */}
                {/* EA PRINTABLE BOARD RATIONALE — STANDALONE (no AppShell)      */}
                {/* Opens in a new tab for print/PDF export.                     */}
                {/* ============================================================ */}
                {import.meta.env.VITE_ENABLE_EA_PROTOTYPE === 'true' && (
                  <Route
                    path="/use-case/board-rationale/print"
                    element={
                      <PrivateRoute>
                        <EAProvider>
                          <EAPrintableBoardRationalePage />
                        </EAProvider>
                      </PrivateRoute>
                    }
                  />
                )}

                {/* ============================================================ */}
                {/* EA PRINTABLE ASSUMPTIONS APPENDIX — STANDALONE (no AppShell) */}
                {/* Opens in a new tab for print/PDF export.                     */}
                {/* ============================================================ */}
                {import.meta.env.VITE_ENABLE_EA_PROTOTYPE === 'true' && (
                  <Route
                    path="/use-case/assumptions/print"
                    element={
                      <PrivateRoute>
                        <EAProvider>
                          <EAPrintableAssumptionsAppendixPage />
                        </EAProvider>
                      </PrivateRoute>
                    }
                  />
                )}

                {/* ============================================================ */}
                {/* EA APP ROUTES — STOREFRONT PROMOTION (de-prefixed)           */}
                {/* EA is now THE application. A single pathless layout route     */}
                {/* mounts EAProvider + AppShell(hideSecondaryNav) once and swaps  */}
                {/* pages through <Outlet/>, preserving EA state across the flow.  */}
                {/*                                                              */}
                {/* Step 1: Personalize          → /home                          */}
                {/* Step 2: Evaluate & Shortlist → /opportunities                 */}
                {/* Step 3: Activate             → /activate                      */}
                {/* Weight settings              → /weights                       */}
                {/* Use case profile             → /opportunity/:id/brief         */}
                {/* Scoring rationale            → /opportunity/:id/rationale     */}
                {/* ROI model                    → /opportunity/:id/roi-model     */}
                {/* Command Center               → /execute/:initiativeId         */}
                {/* Strategy brief (entry point) → /strategy-brief                */}
                {/* Upgrade (conversion surface) → /upgrade                       */}
                {/*                                                              */}
                {/* Legacy /ea/* redirect to the above via EALegacyRedirect.      */}
                {/* ============================================================ */}
                {import.meta.env.VITE_ENABLE_EA_PROTOTYPE === 'true' && (
                  <Route
                    element={
                      <PrivateRoute>
                        <EAProvider>
                          <AppShell hideSecondaryNav={true}>
                            <Outlet />
                          </AppShell>
                        </EAProvider>
                      </PrivateRoute>
                    }
                  >
                    {/* Step 1: Personalize (formerly /ea/personalization) */}
                    <Route path="/home" element={<EAPersonalizationPage />} />

                    {/* Temporary variant routes (layout explorations) */}
                    <Route path="/personalization-variant-a" element={<EAPersonalizationVariantA />} />
                    <Route path="/personalization-variant-b" element={<EAPersonalizationVariantB />} />

                    {/* Weight settings */}
                    <Route path="/weights" element={<EAWeightsPage />} />

                    {/* Step 2: Evaluate & Shortlist */}
                    <Route path="/opportunities" element={<EAOpportunitiesPage />} />

                    {/* Step 3: Activate */}
                    <Route path="/activate" element={<EAActivatePage />} />

                    {/* Use-Case Hub (Full-Content Drilldown v4): one hub shell, tab in the
                        URL segment (brief | rationale | roi-model). The ROI tab reuses the
                        existing EAROIModelPage embedded. Prev/next preserves the tab. */}
                    <Route path="/opportunity/:id/:tab" element={<EAUseCaseHubPage />} />

                    {/* Execute hub (Oct 21 v3): tab in the URL segment
                        (summary | approval | vendors | execution). The bare path redirects to
                        Summary so existing /execute/:id links keep working. The v2 single-page
                        EAInitiativeCommandCenterPage is no longer routed (tag v2-drilldown-redesign). */}
                    <Route path="/execute/:initiativeId/:tab" element={<EAExecuteHubPage />} />
                    <Route path="/execute/:initiativeId" element={<ExecuteHubRedirect />} />

                    {/* Strategy Intelligence Brief — live EA surface entry point */}
                    <Route path="/strategy-brief" element={<EAStrategyIntelligenceBriefPage />} />

                    {/* Upgrade — conversion surface */}
                    <Route path="/upgrade" element={<EAUpgradePage />} />
                  </Route>
                )}

                {/* Legacy /ea/* → de-prefixed routes (bookmarks / back-compat) */}
                {import.meta.env.VITE_ENABLE_EA_PROTOTYPE === 'true' && (
                  <Route path="/ea/*" element={<EALegacyRedirect />} />
                )}

                {/* ============================================================ */}
                {/* Enterprise Version prototype — Phase 2 SHELL (empty routes). */}
                {/* Gated by VITE_ENABLE_ENTERPRISE_PROTOTYPE. Same AppShell as the */}
                {/* storefront; AppShell renders the mode-flip toggle + navy sub-nav. */}
                {/* Surfaces are stubs until Phase 3.                              */}
                {/* ============================================================ */}
                {import.meta.env.VITE_ENABLE_ENTERPRISE_PROTOTYPE === 'true' && (
                  <Route
                    element={
                      <PrivateRoute>
                        <AppShell hideSecondaryNav={true}>
                          <Outlet />
                        </AppShell>
                      </PrivateRoute>
                    }
                  >
                    <Route path="/enterprise" element={<Navigate to="/enterprise/portfolio" replace />} />
                    <Route path="/enterprise/portfolio" element={<PortfolioSurface />} />
                    <Route path="/enterprise/scenarios" element={<ScenariosSurface />} />
                    <Route path="/enterprise/decisions" element={<DecisionsSurface />} />
                    <Route path="/enterprise/reports" element={<ReportsSurface />} />
                  </Route>
                )}

                {/* Legacy Pro routes — removed by storefront promotion.
                    Bookmarks redirect to the EA app. */}
                <Route path="/pro/*" element={<Navigate to="/home" replace />} />

                {/* ============================================================ */}
                {/* EA FOCUS MODE ROUTES                                         */}
                {/* These routes support EA Focus Mode:                          */}
                {/* - When accessed from EA (fromEA=true): AppShell (no sidebar) */}
                {/* - When accessed normally: MainAppLayout (with sidebar)       */}
                {/* ============================================================ */}
                
                {/* Client Profile - EA Focus Mode enabled */}
                <Route
                  path="/client-profile"
                  element={
                    <PrivateRoute>
                      <EAFocusRouteWrapper>
                        <ClientProfileAuthRoute />
                      </EAFocusRouteWrapper>
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/client-profile/:clientId"
                  element={
                    <PrivateRoute>
                      <EAFocusRouteWrapper>
                        <ClientProfileRoute />
                      </EAFocusRouteWrapper>
                    </PrivateRoute>
                  }
                />

                {/* Document Upload — rebuilt from design variant 1a. The page owns its own
                    back bar, so it mounts WITHOUT EAFocusRouteWrapper rather than carrying
                    two sets of chrome. The previous DocumentManagementPage is unchanged and
                    still reachable at /documents/:clientId. */}
                <Route
                  path="/documents"
                  element={
                    <PrivateRoute>
                      <EADocumentUploadPage />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/documents/:clientId"
                  element={
                    <PrivateRoute>
                      <EAFocusRouteWrapper>
                        <DocumentManagementRoute />
                      </EAFocusRouteWrapper>
                    </PrivateRoute>
                  }
                />

                {/* Main app routes use MainAppLayout (sidebar + header) */}
                <Route
                  path="*"
                  element={
                    <PrivateRoute>
                      <MainAppLayout>
                        <Routes>
                          <Route path="/" element={<Navigate to="/home" replace />} />

                          {/* Client Configurator / Data Input */}
                          <Route path="client-configurator" element={<ClientConfiguratorPage />} />
                          <Route path="client-configurator/:clientId" element={<ClientConfiguratorPage />} />
                          <Route path="client-data-input/:clientId" element={<ClientConfiguratorPage />} />

                          {/* NOTE: /documents and /client-profile routes moved above for EA Focus Mode */}

                          {/* Action Board */}
                          <Route path="action-board" element={<ActionBoardPriority />} />
                          <Route path="action-board/:clientId" element={<ActionBoardRoute />} />

                          {/* Matrix */}
                          <Route
                            path="matrix"
                            element={
                              <MatrixPage
                                __firebase_config={JSON.stringify(firebaseConfig)}
                                __looker_studio_embed_url={lookerStudioEmbedUrl}
                                __initial_auth_token={null}
                                __app_id="dva-frontend-vite"
                              />
                            }
                          />

                          {/* Use Case Results */}
                          <Route path="results" element={<UseCaseResults />} />

                          {/* Playbooks */}
                          <Route path="playbook" element={<PlaybookPage />} />
                          <Route path="playbook-draft/:id" element={<PlaybookDraftPage />} />

                          {/* Strategy & Summaries */}
                          <Route path="strategic-alignment" element={<StrategicAlignmentAuthRoute />} />
                          <Route path="capabilities-summary" element={<CapabilitiesSummaryPage />} />
                          <Route path="strategy-summary" element={<ComprehensiveStrategySummaryPage />} />
                          <Route path="summary/:type" element={<StrategySummaryPage />} />

                          {/* Use Case Profile */}
                          <Route path="usecase/:id" element={<UseCaseProfilePage />} />

                          {/* QA */}
                          <Route path="qa" element={<Navigate to="/qa/selector" replace />} />
                          <Route path="qa/selector" element={<UseCaseSelector />} />
                          <Route path="qa/:useCaseId" element={<UseCaseQADashboard />} />

                          {/* Financial QA */}
                          <Route path="financial-qa" element={<FinancialQAPage />} />
                          <Route path="financial-qa/:clientId" element={<FinancialQAPage />} />

                          {/* Utilities */}
                          <Route path="chat" element={<ChatPage />} />
                          <Route path="settings" element={<SettingsPage />} />
                          <Route path="logout" element={<LogoutPage />} />

                          {/* Dev/Test */}
                          <Route path="test-survey" element={<TestSurveyRoute />} />

                          {/* 404 → Home */}
                          <Route path="*" element={<Navigate to="/home" replace />} />
                        </Routes>
                      </MainAppLayout>
                    </PrivateRoute>
                  }
                />
              </Routes>
              </HelpProvider>
            </Router>
          </PageContextProvider>
        </FilterProvider>
      </ClientProvider>
    </AuthProvider>
  );
}